import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface SimpleProfessionalFormProps {
  title?: string;
}

// Type for Supabase function response
interface ApplicationResponse {
  success: boolean;
  message?: string;
  applicationId?: string;
}

const SimpleProfessionalForm: React.FC<SimpleProfessionalFormProps> = ({
  title = "Join the SeniorThrive Professional Network"
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    professionalCategory: '',
    profession: '',
    company: '',
    bio: '',
    website: '',
    linkedinProfile: '',
    licenseNumber: '',
    licenseState: '',
    zipCode: '',
    serviceRadius: 25,
    specialties: [] as string[],
    agreeToTerms: false
  });

  // Professional categories for the primary dropdown
  const professionalCategories = [
    { value: 'geriatric_care_manager', label: 'Geriatric Care Manager' },
    { value: 'ot_pt', label: 'Occupational or Physical Therapist' },
    { value: 'caps_specialist', label: 'CAPS Specialist' },
    { value: 'elder_law_attorney', label: 'Elder Law Attorney' },
    { value: 'financial_advisor', label: 'Financial Advisor / Estate Planner' },
    { value: 'caregiver_agency', label: 'Professional Caregiver or Agency' },
    { value: 'social_worker', label: 'Social Worker / Case Manager' },
    { value: 'home_modification', label: 'Home Modification Contractor' },
    { value: 'clinician', label: 'Nurse, Clinician, or Healthcare Provider' },
    { value: 'other', label: 'Other Senior Services' }
  ];

  // Dynamic specialties mapped by professional category
  const specialtiesByCategory: Record<string, Array<{ value: string; label: string }>> = {
    geriatric_care_manager: [
      { value: 'care_coordination', label: 'Care Coordination' },
      { value: 'dementia_care', label: 'Dementia Care' },
      { value: 'chronic_disease', label: 'Chronic Disease Management' },
      { value: 'family_mediation', label: 'Family Mediation' },
      { value: 'discharge_planning', label: 'Discharge Planning' }
    ],
    ot_pt: [
      { value: 'falls_prevention', label: 'Falls Prevention' },
      { value: 'home_safety', label: 'Home Safety Assessment' },
      { value: 'mobility_training', label: 'Mobility Training' },
      { value: 'adaptive_equipment', label: 'Adaptive Equipment' },
      { value: 'balance_strength', label: 'Balance & Strength' },
      { value: 'adl', label: 'Activities of Daily Living' }
    ],
    caps_specialist: [
      { value: 'bathroom_mods', label: 'Bathroom Modifications' },
      { value: 'kitchen_accessibility', label: 'Kitchen Accessibility' },
      { value: 'stairway_safety', label: 'Stairway Safety' },
      { value: 'lighting_visibility', label: 'Lighting & Visibility' },
      { value: 'universal_design', label: 'Universal Design' },
      { value: 'smart_home', label: 'Smart Home Integration' }
    ],
    elder_law_attorney: [
      { value: 'estate_planning', label: 'Estate Planning' },
      { value: 'medicaid_planning', label: 'Medicaid Planning' },
      { value: 'guardianship', label: 'Guardianship' },
      { value: 'ltc_planning', label: 'Long-Term Care Planning' },
      { value: 'veterans_benefits', label: 'Veterans Benefits' }
    ],
    financial_advisor: [
      { value: 'retirement_planning', label: 'Retirement Planning' },
      { value: 'ltc_insurance', label: 'Long-Term Care Insurance' },
      { value: 'medicare_planning', label: 'Medicare Planning' },
      { value: 'estate_preservation', label: 'Estate Preservation' },
      { value: 'tax_strategy', label: 'Tax Strategy' }
    ],
    caregiver_agency: [
      { value: 'personal_care', label: 'Personal Care' },
      { value: 'companionship', label: 'Companionship' },
      { value: 'medication_reminders', label: 'Medication Reminders' },
      { value: 'meal_prep', label: 'Meal Preparation' },
      { value: 'transportation', label: 'Transportation' },
      { value: 'respite_care', label: 'Respite Care' }
    ],
    social_worker: [
      { value: 'resource_navigation', label: 'Resource Navigation' },
      { value: 'crisis_intervention', label: 'Crisis Intervention' },
      { value: 'family_counseling', label: 'Family Counseling' },
      { value: 'benefits_enrollment', label: 'Benefits Enrollment' },
      { value: 'care_transitions', label: 'Care Transitions' }
    ],
    home_modification: [
      { value: 'accessibility_ramps', label: 'Accessibility Ramps' },
      { value: 'grab_bars', label: 'Grab Bars & Rails' },
      { value: 'walk_in_tubs', label: 'Walk-in Tubs' },
      { value: 'widening_doorways', label: 'Widening Doorways' },
      { value: 'flooring_safety', label: 'Flooring Safety' },
      { value: 'threshold_removal', label: 'Threshold Removal' }
    ],
    clinician: [
      { value: 'chronic_disease', label: 'Chronic Disease Management' },
      { value: 'wound_care', label: 'Wound Care' },
      { value: 'medication_management', label: 'Medication Management' },
      { value: 'health_monitoring', label: 'Health Monitoring' },
      { value: 'palliative_care', label: 'Palliative Care' },
      { value: 'telehealth', label: 'Telehealth' }
    ],
    other: [
      { value: 'wellness_programs', label: 'Wellness Programs' },
      { value: 'nutrition_counseling', label: 'Nutrition Counseling' },
      { value: 'senior_fitness', label: 'Senior Fitness' },
      { value: 'tech_assistance', label: 'Technology Assistance' },
      { value: 'transportation', label: 'Transportation' }
    ]
  };

  // Get available specialties based on selected category
  const getAvailableSpecialties = () => {
    if (!formData.professionalCategory) return [];
    return specialtiesByCategory[formData.professionalCategory] || specialtiesByCategory.other;
  };

  // Handle category change - reset specialties when category changes
  const handleCategoryChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      professionalCategory: value,
      specialties: [] // Reset when category changes
    }));
  };

  // Supabase function mutation
  const mutation = useMutation({
    mutationFn: async (variables: any): Promise<ApplicationResponse> => {
      try {
        const { data, error } = await supabase.functions.invoke('submit-professional-application', {
          body: {
            profession: 'PROFESSIONAL',
            ...variables
          }
        });
        
        if (error) {
          throw new Error(error.message || 'Submission failed');
        }
        return data;
      } catch (error) {
        console.error('Application submission failed:', error);
        throw new Error('Unable to submit application. Please try again later or contact support.');
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        navigate('/professional-network/success');
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    },
    onError: (error: any) => {
      console.error('Application submission error:', error);
      toast({
        title: "Submission Error",
        description: error?.message || "There was an error submitting your application. Please try again or contact support.",
        variant: "destructive"
      });
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreeToTerms) {
      toast({
        title: "Agreement Required",
        description: "Please agree to the terms and conditions to continue.",
        variant: "destructive"
      });
      return;
    }

    // Build submission variables
    const variables = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      companyName: formData.company,
      bio: formData.bio,
      website: formData.website,
      linkedinUrl: formData.linkedinProfile,
      licenseNumber: formData.licenseNumber,
      licenseState: formData.licenseState,
      zipCode: formData.zipCode,
      serviceRadiusMiles: formData.serviceRadius,
      specialties: formData.specialties,
      areasOfExpertise: formData.specialties,
      professionalCategory: formData.professionalCategory,
      roleTitle: formData.profession || null
    };

    // Add UTM and meta data
    const params = new URLSearchParams(window.location.search);
    const meta = {
      pagePath: window.location.pathname,
      referrer: document.referrer || '',
      utmSource: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
      utmMedium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
      utmContent: params.get('utm_content') || sessionStorage.getItem('utm_content') || '',
      utmTerm: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
      source: 'website'
    };

    mutation.mutate({ ...variables, ...meta });
  };

  const handleSpecialtyChange = (specialty: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      specialties: checked 
        ? [...prev.specialties, specialty] 
        : prev.specialties.filter(s => s !== specialty)
    }));
  };

  // Main application form
  return (
    <Card className="w-full max-w-none mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">{title}</CardTitle>
        <CardDescription className="text-center">
          Connect with families and older adults who need your expertise
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Professional Type Selector - Primary Segmentation */}
          <div className="space-y-2">
            <Label htmlFor="professionalCategory">Professional Type *</Label>
            <Select 
              value={formData.professionalCategory} 
              onValueChange={handleCategoryChange}
              required
            >
              <SelectTrigger><SelectValue placeholder="Select your professional type" /></SelectTrigger>
              <SelectContent className="bg-background border border-border shadow-lg z-50">
                {professionalCategories.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Professional Information */}
          <div className="space-y-2">
            <Label htmlFor="profession">Your Role/Title</Label>
            <Input
              id="profession"
              type="text"
              placeholder="Example: Falls Prevention Specialist, Senior Care Manager"
              value={formData.profession}
              onChange={(e) => setFormData(prev => ({ ...prev, profession: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company/Practice Name</Label>
            <Input
              id="company"
              type="text"
              placeholder="Your organization name"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
            />
          </div>

          {/* Dynamic Areas of Focus - Only show after category is selected */}
          {formData.professionalCategory && (
            <div className="space-y-2 animate-in fade-in-50 slide-in-from-top-2 duration-300">
              <Label>Areas of Focus (select all that apply)</Label>
              <p className="text-sm text-muted-foreground mb-2">
                These help families find you for specific needs
              </p>
              <div className="grid grid-cols-2 gap-2">
                {getAvailableSpecialties().map((specialty) => (
                  <div key={specialty.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={specialty.value}
                      checked={formData.specialties.includes(specialty.value)}
                      onCheckedChange={(checked) => handleSpecialtyChange(specialty.value, checked as boolean)}
                    />
                    <Label 
                      htmlFor={specialty.value} 
                      className="text-sm font-normal cursor-pointer"
                    >
                      {specialty.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Location & Service Area */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="zipCode">Service Area Zip Code *</Label>
              <Input
                id="zipCode"
                type="text"
                placeholder="e.g., 12345"
                value={formData.zipCode}
                onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="serviceRadius">Service Radius (miles)</Label>
              <Input
                id="serviceRadius"
                type="number"
                min="0"
                placeholder="25"
                value={formData.serviceRadius}
                onChange={(e) => setFormData(prev => ({ ...prev, serviceRadius: parseInt(e.target.value) || 25 }))}
              />
            </div>
          </div>

          {/* License Information (Optional) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="licenseNumber">License Number (if applicable)</Label>
              <Input
                id="licenseNumber"
                type="text"
                placeholder="e.g., #12345"
                value={formData.licenseNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, licenseNumber: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="licenseState">License State</Label>
              <Select 
                value={formData.licenseState} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, licenseState: value }))}
              >
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="bg-background border border-border shadow-lg z-50">
                  <SelectItem value="AL">Alabama</SelectItem>
                  <SelectItem value="AK">Alaska</SelectItem>
                  <SelectItem value="AZ">Arizona</SelectItem>
                  <SelectItem value="AR">Arkansas</SelectItem>
                  <SelectItem value="CA">California</SelectItem>
                  <SelectItem value="CO">Colorado</SelectItem>
                  <SelectItem value="CT">Connecticut</SelectItem>
                  <SelectItem value="DE">Delaware</SelectItem>
                  <SelectItem value="FL">Florida</SelectItem>
                  <SelectItem value="GA">Georgia</SelectItem>
                  <SelectItem value="HI">Hawaii</SelectItem>
                  <SelectItem value="ID">Idaho</SelectItem>
                  <SelectItem value="IL">Illinois</SelectItem>
                  <SelectItem value="IN">Indiana</SelectItem>
                  <SelectItem value="IA">Iowa</SelectItem>
                  <SelectItem value="KS">Kansas</SelectItem>
                  <SelectItem value="KY">Kentucky</SelectItem>
                  <SelectItem value="LA">Louisiana</SelectItem>
                  <SelectItem value="ME">Maine</SelectItem>
                  <SelectItem value="MD">Maryland</SelectItem>
                  <SelectItem value="MA">Massachusetts</SelectItem>
                  <SelectItem value="MI">Michigan</SelectItem>
                  <SelectItem value="MN">Minnesota</SelectItem>
                  <SelectItem value="MS">Mississippi</SelectItem>
                  <SelectItem value="MO">Missouri</SelectItem>
                  <SelectItem value="MT">Montana</SelectItem>
                  <SelectItem value="NE">Nebraska</SelectItem>
                  <SelectItem value="NV">Nevada</SelectItem>
                  <SelectItem value="NH">New Hampshire</SelectItem>
                  <SelectItem value="NJ">New Jersey</SelectItem>
                  <SelectItem value="NM">New Mexico</SelectItem>
                  <SelectItem value="NY">New York</SelectItem>
                  <SelectItem value="NC">North Carolina</SelectItem>
                  <SelectItem value="ND">North Dakota</SelectItem>
                  <SelectItem value="OH">Ohio</SelectItem>
                  <SelectItem value="OK">Oklahoma</SelectItem>
                  <SelectItem value="OR">Oregon</SelectItem>
                  <SelectItem value="PA">Pennsylvania</SelectItem>
                  <SelectItem value="RI">Rhode Island</SelectItem>
                  <SelectItem value="SC">South Carolina</SelectItem>
                  <SelectItem value="SD">South Dakota</SelectItem>
                  <SelectItem value="TN">Tennessee</SelectItem>
                  <SelectItem value="TX">Texas</SelectItem>
                  <SelectItem value="UT">Utah</SelectItem>
                  <SelectItem value="VT">Vermont</SelectItem>
                  <SelectItem value="VA">Virginia</SelectItem>
                  <SelectItem value="WA">Washington</SelectItem>
                  <SelectItem value="WV">West Virginia</SelectItem>
                  <SelectItem value="WI">Wisconsin</SelectItem>
                  <SelectItem value="WY">Wyoming</SelectItem>
                  <SelectItem value="DC">District of Columbia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>


          {/* Website and LinkedIn */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                placeholder="https://yourwebsite.com"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedinProfile">LinkedIn Profile</Label>
              <Input
                id="linkedinProfile"
                type="url"
                placeholder="https://linkedin.com/in/yourprofile"
                value={formData.linkedinProfile}
                onChange={(e) => setFormData(prev => ({ ...prev, linkedinProfile: e.target.value }))}
              />
            </div>
          </div>

          {/* Terms Agreement */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={formData.agreeToTerms}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeToTerms: checked as boolean }))}
              required
            />
            <Label htmlFor="terms" className="text-sm leading-relaxed">
              I agree to the{' '}
              <Link to="/referral-agreement" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Referral Agreement
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              *
            </Label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="default"
            size="lg"
            className="w-full"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Submitting...' : 'Plug Your Services In'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default SimpleProfessionalForm;